import {
  Modal,
  Button,
  Group,
  Select,
  Radio,
  Flex,
  Box,
  rem,
  Tooltip,
  Text,
  Loader,
  Skeleton,
} from "@mantine/core";
import useStyles from "./style.js";
import { useSelector } from "react-redux";
import { useState, useEffect } from "react";
import { DropDownArrowSvg } from "../Svgs/DropDownArrowSvg.jsx";
import { calculateDistanceMiles } from "../../services/UserTracking.js";
import { getDeviceLocation } from "../../utils/geolocation.js";
import { getAllFacilitiesModal } from "../../services/facility.js";
// Build full address string from facility object for Google API
const buildFullAddress = (facility) => {
  if (!facility) return null;
  const city = facility.city || facility.addressLineTwo || facility.address2;
  const parts = [
    facility.address,
    city,
    facility.state,
    facility.zipcode,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : null;
};

// Build address string from client's form1 currentAddress fields
const buildClientAddress = (client) => {
  if (!client?.form1) return null;
  const { currentAddress, currentCity, currentState, currentZipCode } = client.form1;
  const parts = [currentAddress, currentCity, currentState, currentZipCode].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : null;
};

// Build grouped Select options from client's registered address + otherLocations
const getClientAddressOptions = (client) => {
  if (!client) return [];
  const options = [];
  const currentAddr = buildClientAddress(client);
  if (currentAddr) {
    options.push({
      value: "current",
      label: currentAddr,
      group: "Current Address",
      fullAddress: currentAddr,
      obj: {
        label: "Current Address",
        address: client.form1?.currentAddress || "",
        city: client.form1?.currentCity || "",
        state: client.form1?.currentState || "",
        zipCode: client.form1?.currentZipCode || "",
      },
    });
  }
  (client.form1?.otherLocations || []).forEach((loc, i) => {
    const addr = [loc.address, loc.city, loc.state, loc.zipCode]
      .filter(Boolean)
      .join(", ");
    if (addr) {
      options.push({
        value: `other_${i}`,
        label: `${loc.label} – ${loc.address}`,
        group: "Other Locations",
        fullAddress: addr,
        obj: loc,
      });
    }
  });
  return options;
};

function SelectCheckInType({
  opened,
  close,
  open,
  handleModal,
  selectedFacility,
  setSelectedFacility,
  selectedClient,
  setSelectedClient,
  setDisableClockBtn,
  selectedService,
  setSelectedService,
  setEvvLocationData,
}) {
  const { classes } = useStyles();
  // const navigate = useNavigate();
  const { profile } = useSelector((state) => state?.profile);
  const { allPatients } = useSelector((state) => state?.patient);
  const { allFacilities } = useSelector((state) => state?.facility);
  const [selectedType, setSelectedType] = useState();
  const [facilityValue, setFacilityValue] = useState(null);
  const [warningText, setWarningText] = useState(null);
  // const [origins, setOrigins] = useState(null);
  // const [destinations, setDestinations] = useState(null);
  const [miles, setMiles] = useState(null);
  const [milesError, setMilesError] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const isStaff = profile?.user?.type?.toLowerCase() === "staff";
  const [locationPermission, setLocationPermission] = useState("prompt");
  const [overrideAddress, setOverrideAddress] = useState("");
  const [selectedPickupLocation, setSelectedPickupLocation] = useState(null);
  const [selectedPickupObj, setSelectedPickupObj] = useState(null);
  const [isClockLoading, setIsClockLoading] = useState(false);
  const [isLocationLoading, setIsLocationLoading] = useState(false);

  // const facilities =
  //   profile?.user?.facilityId?.filter(
  //     (_f) =>
  //       (_f?.checkInType === selectedType ||
  //         _f?.checkInType === "no-clockin") &&
  //       _f?.deleted === false
  //   ) || [];

  let facilities = [];

  if (isStaff) {
    facilities =
      profile?.user?.facilityId?.filter(
        (_f) =>
          _f?.checkInType === selectedType &&
          _f?.checkInType !== "no-clockin" &&
          _f?.deleted === false,
      ) || [];
  } else {
    facilities =
      allFacilities?.facilities?.filter(
        (_f) =>
          _f?.checkInType === selectedType &&
          _f?.checkInType !== "no-clockin" &&
          _f?.deleted === false,
      ) || [];
  }

  // Find the EVV facility for the selected client by matching client's facilityId
  const getClientEvvFacility = () => {
    if (!selectedClient) return null;
    const client = allPatients?.find((_p) => _p?.id === selectedClient);
    const clientFacilityIds = (client?.facilityId || []).map((f) =>
      typeof f === "string" ? f : f?.id || f?._id,
    );
    // Find a facility the client belongs to that has EVV enabled
    return (
      facilities?.find(
        (f) =>
          clientFacilityIds.includes(f?.id || f?._id) &&
          f?.evvRequired === "yes",
      ) || null
    );
  };

  const clientEvvFacility =
    selectedType === "client" ? getClientEvvFacility() : null;

  // Compute EVV max distance for the current selection (facility or client)
  const currentMaxDistance = (() => {
    if (selectedType === "facility" && selectedFacility) {
      return selectedFacility?.evvMaxDistance || 0.25;
    }
    if (selectedType === "client" && clientEvvFacility) {
      return clientEvvFacility.evvMaxDistance || 0.25;
    }
    return 0.25;
  })();

  const clockInHandler = () => {
    handleModal("clock in");
  };

  const onChangeFacility = (value) => {
    setSelectedClient(null);
    // setAddressNotFound(false);
    setMiles(null);
    setMilesError(false);
    setFacilityValue(value);
    const facilities = isStaff
      ? profile?.user?.facilityId
      : allFacilities?.facilities;
    let facility_index = facilities?.findIndex((_f) => _f?.id === value);
    // set facility object for payload
    setSelectedFacility(facilities[facility_index]);
    // Call immediately — only when GPS is on
    if (userLocation?.source === "gps") calculateDistance({ facilityObj: facilities[facility_index] });

    if (!facilities[facility_index]?.checkInType) {
      setWarningText("Ask admin to assign chock in type to the facility");
    } else {
      setWarningText(null);
    }
  };

  // Fires when GPS location resolves after a selection was already made,
  // or when overrideAddress changes. Only runs when GPS is available.
  useEffect(() => {
    if (userLocation?.source !== "gps") return;
    if (selectedType === "client") {
      if (selectedClient) calculateDistance({ clientId: selectedClient });
    } else {
      if (selectedFacility) calculateDistance({ facilityObj: selectedFacility });
    }
  }, [userLocation, overrideAddress]);

  const onChangeClient = (value) => {
    setSelectedClient(value);
    setOverrideAddress("");
    setSelectedPickupLocation("current");
    setSelectedPickupObj(null);
    setMiles(null);
    setMilesError(false);
    const client = allPatients?.find((_p) => _p?.id === value);
    const addr = buildClientAddress(client);
    setSelectedAddress(addr || null);
    if (userLocation?.source === "gps" && addr) {
      calculateDistance({ clientId: value, overrideAddressParam: "" });
    }
  };

  const handlePickupLocationChange = (value) => {
    const client = allPatients?.find((_p) => _p?.id === selectedClient);
    const options = getClientAddressOptions(client);
    const selected = options.find((o) => o.value === value);
    setSelectedPickupLocation(value);
    setSelectedPickupObj(selected?.obj || null);
    // "current" = use client's registered address (empty overrideAddress falls through to buildClientAddress)
    setOverrideAddress(value === "current" || !value ? "" : selected?.fullAddress || "");
  };

  const onChangeService = (value) => {
    setSelectedService(value);
  };

  const radioChangeHandler = (value) => {
    setFacilityValue(null);
    setSelectedType(value);
    // setSelectedType({f_type: null, f_id: null});
    // setSelectedType({f_type: value, f_id: null});
  };

  let types = [];
  if (isStaff) {
    types = profile?.user?.facilityId
      ?.filter((f) => f?.deleted === false)
      ?.map((f) => f?.checkInType);
  } else {
    types = allFacilities?.facilities
      ?.filter((f) => f?.deleted === false)
      ?.map((f) => f?.checkInType);
  }

  const checkInTypes = new Set(types);

  // const checkInTypes = new Set(
  //   profile?.user?.facilityId
  //   ?.filter((f) => f?.deleted === false)
  //   ?.map((f) => f?.checkInType)
  // );

  const hasFacility = checkInTypes.has("facility");
  const hasClient = checkInTypes.has("client");

  const boxWidth = hasFacility && hasClient ? "48%" : "100%";

  useEffect(() => {
    if (profile?.user?.facilityId) {
      setDisableClockBtn(false);
      for (var a = 0; a < profile?.user?.facilityId?.length; a++) {
        let i = profile?.user?.facilityId[a];
        if (i?.checkInType !== "no-clockin") {
          setDisableClockBtn(false);
        }
      }
    }
  }, []);

  const icon = (
    <DropDownArrowSvg
      color={"#94A3B8"}
      style={{ width: rem(1), height: rem(1) }}
    />
  );

  const handleClose = () => {
    setSelectedFacility(null);
    setSelectedClient(null);
    setSelectedType(null);
    setFacilityValue(null);
    setWarningText(null);
    close();
  };

  useEffect(() => {
    const fetchLocation = async () => {
      setIsLocationLoading(true);
      const loc = await getDeviceLocation();
      if (loc) {
        setUserLocation(loc);
        setLocationPermission("granted");
      } else {
        setLocationPermission("denied");
      }
      setIsLocationLoading(false);
    };
    fetchLocation();
  }, []);

  // Reset all internal state when the modal opens to prevent stale data
  useEffect(() => {
    if (opened) {
      setSelectedType(null);
      setFacilityValue(null);
      setSelectedFacility(null);
      setSelectedClient(null);
      setWarningText(null);
      setMiles(null);
      setMilesError(false);
      setSelectedAddress(null);
      setOverrideAddress("");
      setSelectedPickupLocation(null);
      setSelectedPickupObj(null);
      setEvvLocationData?.(null);
    }
  }, [opened]);

  useEffect(() => {
    if (selectedType) {
      setMiles(null);
      setSelectedAddress(null);
      setSelectedFacility(null);
      setSelectedClient(null);
      setFacilityValue(null);
      setOverrideAddress("");
      setSelectedPickupLocation(null);
      setSelectedPickupObj(null);
    }
  }, [selectedType]);

  const calculateDistance = async ({ clientId = selectedClient, facilityObj = selectedFacility, overrideAddressParam = overrideAddress } = {}) => {
    if (!userLocation) return;
    try {
      setIsClockLoading(true);
      const origins = `${userLocation.lat},${userLocation.lng}`;

      // const result = await axios.get(
      //   `https://maps.googleapis.com/maps/api/distancematrix/json`,
      //   {
      //     params: {
      //       origins: 'New%20York%20City%2C%20NY',
      //       destinations: 'Washington%2C%20DC',
      //       key: API_KEY,
      //       units: 'imperial' // or 'imperial'
      //     }
      //   }
      // );
      let destination = buildFullAddress(facilityObj);
      // Use custom clock-in location address if configured
      if (selectedType === "facility" && facilityObj?.clockInLocation?.type === "custom" && facilityObj.clockInLocation.customAddress?.address) {
        const ca = facilityObj.clockInLocation.customAddress;
        destination = [ca.address, ca.city, ca.state, ca.zipcode].filter(Boolean).join(", ");
      }
      if (selectedType === "client") {
        const client = allPatients?.find((_p) => _p?.id === clientId);
        destination = overrideAddressParam || buildClientAddress(client) || null;
      }

      if (!destination) {
        setSelectedAddress(null);
        setMiles(null);
        setMilesError(false);
        setIsClockLoading(false);
        return;
      }

      let payload = {
        origin: origins,
        destination,
        // units: 'imperial' // or 'imperial'
        mode: "WALKING", // or 'imperial'
      };
      let result = await calculateDistanceMiles(payload);
      if (result?.tracking) {
        let legItem = result?.tracking?.legs?.[0];
        let meeterDistance = legItem?.distance?.value;
        let calculatedMiles =
          Math.round((meeterDistance * 0.000621371192 + Number.EPSILON) * 100) /
          100;

        setMiles(calculatedMiles);
        setSelectedAddress(legItem?.end_address);
        // Compute max distance using the arguments passed in (not stale closure state).
        // On first client selection, selectedClient hasn't updated yet, so clientId param
        // is the only reliable source.
        let effectiveMaxDistance = 0.25;
        if (selectedType === "facility") {
          effectiveMaxDistance = facilityObj?.evvMaxDistance || 0.25;
        } else if (selectedType === "client") {
          const clientForDist = allPatients?.find((_p) => _p?.id === clientId);
          const clientFacilityIds = (clientForDist?.facilityId || []).map((f) =>
            typeof f === "string" ? f : f?.id || f?._id,
          );
          const evvFacility = facilities?.find(
            (f) =>
              clientFacilityIds.includes(f?.id || f?._id) &&
              f?.evvRequired === "yes",
          );
          effectiveMaxDistance = evvFacility?.evvMaxDistance || 0.25;
        }
        if (calculatedMiles > effectiveMaxDistance) {
          setMilesError(true);
        } else {
          setMilesError(false);
        }
        // Pass EVV location data up to parent
        setEvvLocationData?.({
          evvLocation: {
            lat: userLocation?.lat,
            lng: userLocation?.lng,
            accuracy: userLocation?.accuracy || null,
          },
          evvDistanceToSite: calculatedMiles,
          evvMatchedAddress: legItem?.end_address || null,
          locationSource: userLocation?.source || null,
          overrideAddress: overrideAddressParam || null,
          pickupLocation: selectedPickupObj || null,
        });
      } else {
        setSelectedAddress(destination);
        setMiles(null);
        // Still pass location data even when distance calc fails
        if (userLocation?.lat && userLocation?.lng) {
          setEvvLocationData?.({
            evvLocation: {
              lat: userLocation?.lat,
              lng: userLocation?.lng,
              accuracy: userLocation?.accuracy || null,
            },
            evvDistanceToSite: null,
            evvMatchedAddress: destination || null,
            locationSource: userLocation?.source || null,
            overrideAddress: overrideAddressParam || null,
            pickupLocation: selectedPickupObj || null,
          });
        }
      }
    } catch (err) {
      console.error(err);
      setIsClockLoading(false); // 🔥 stop loading
    } finally {
      setIsClockLoading(false); // 🔥 stop loading
    }
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={handleClose}
        size="lg"
        title="Clock In"
        centered
        radius={16}
        zIndex={1000}
        overlayProps={{
          backgroundOpacity: 0.55,
          blur: 3,
        }}
        className={classes.modal}
      >
        {/* <div className={classes.clockIconParent}>
          <img src={trackerIcon} alt="Tracker Icon" />
        </div>
        <p className={classes.warn}>Select Check In Type</p> */}
        {/* 
        <Select
          className={classes.facilityModal}
          label="Select Facility"
          size="md"
          data={profile?.user?.facilityId?.map((_f) => ({
            label: _f?.name,
            value: _f?.id,
          }))}
          placeholder="Select Facility"
          value={facilityValue}
          onChange={onChangeFacility}
          //disabled={view}
        /> */}

        <Flex align={"center"} justify="center" gap="md">
          <Box
            className={classes.noticeBox}
            style={{
              background: `${milesError ? "#FECDCA" : "#a6f4c580"}`,
              color: `${milesError ? "#B42318" : "#027A48"}`,
            }}
          >
            <Box
              className={classes.notice}
              style={{ color: `${milesError ? "#B42318" : "#027A48"}` }}
            >
              Notice
            </Box>
            {milesError ? (
              <Box className={classes.noticeText}>
                You can't Clock in until you are within{" "}
                <b>{currentMaxDistance}</b> Miles of the address
              </Box>
            ) : (
              <Box className={classes.noticeText}>You are set to Clock In</Box>
            )}
          </Box>
        </Flex>
        {checkInTypes.has("facility") || checkInTypes.has("client") ? (
          <Radio.Group
            name="check_in_type"
            label="Clock-in Type"
            required
            value={selectedType}
            onChange={radioChangeHandler}
            mt={10}
          >
            <Group mt="xs" className={classes.radioGroup}>
              {checkInTypes.has("facility") && (
                <Box
                  className={classes.radioBox}
                  style={{
                    cursor: "pointer",
                    border:
                      selectedType === "facility" ? "1px solid #FEB273" : "",
                    backgroundColor:
                      selectedType === "facility" ? "#feb2732b" : "",
                    width: boxWidth,
                  }}
                  onClick={() => setSelectedType("facility")}
                >
                  <Radio
                    value="facility"
                    label="Facility"
                    variant="outline"
                    size="sm"
                    color="orange"
                  />
                </Box>
              )}
              {checkInTypes.has("client") && (
                <Box
                  className={classes.radioBox}
                  style={{
                    cursor: "pointer",
                    border:
                      selectedType === "client" ? "1px solid #FEB273" : "",
                    backgroundColor:
                      selectedType === "client" ? "#feb2732b" : "",
                    width: boxWidth,
                  }}
                  onClick={() => setSelectedType("client")}
                >
                  <Radio
                    value="client"
                    label="Client"
                    variant="outline"
                    size="sm"
                    color="orange"
                  />
                </Box>
              )}
            </Group>
          </Radio.Group>
        ) : (
          <Text ff={"Inter"} fz={14} fw={500} color="red" my={20} ta={"center"}>
            No Facility clock-in type "Facility" or "Client" assigned
          </Text>
        )}

        {/* <Group mt="xs">
            {profile?.user?.facilityId?.find(
              (_f) => _f?.checkInType === "facility"
            ) && <Radio value="facility" label="Facility Type" />}
            {profile?.user?.facilityId?.find(
              (_f) => _f?.checkInType === "client"
            ) && <Radio value="client" label="Client Type" />}
          </Group> */}

        {selectedType === "facility" && (
          <Tooltip
            label={`${
              selectedType === "facility" || selectedType === "no-clockin"
                ? "Facility"
                : "Client"
            } chock-in type is not assigned to facility`}
            position="bottom"
            withArrow
            disabled={facilities?.length > 0}
          >
            <div>
              <Select
                mt={10}
                label="Select Facility"
                size="md"
                data={facilities?.map((_f) => ({
                  label: _f?.name,
                  value: _f?.id,
                }))}
                placeholder="Select Facility"
                value={facilityValue}
                rightSectionPointerEvents="none"
                rightSection={icon}
                onChange={onChangeFacility}

                //disabled={view}
              />
            </div>
          </Tooltip>
        )}

        {selectedType === "client" && (
          <Select
            label="Select Client"
            size="md"
            data={allPatients
              ?.filter(
                (_p) =>
                  _p?.deleted === false &&
                  _p?.active === true &&
                  // Exclude clients whose facilities are all "no-clockin"
                  _p?.facilityId?.some((fRef) => {
                    const fId =
                      typeof fRef === "string" ? fRef : fRef?.id || fRef?._id;
                    return facilities?.some((f) => (f?.id || f?._id) === fId);
                  }),
              )
              ?.map((_p) => ({
                label: _p?.name,
                value: _p?.id,
              }))}
            value={selectedClient}
            placeholder="Select Client"
            onChange={onChangeClient}
            rightSectionPointerEvents="none"
            rightSection={icon}
            mt={10}
            //disabled={view}
          />
        )}

        {selectedType === "client" && (
          <Select
            // className={classes.facilityModal}
            label="Select Service"
            size="md"
            data={allPatients
              ?.filter(
                (_p) =>
                  _p?.deleted === false &&
                  _p?.active === true &&
                  _p?.id === selectedClient,
              )
              ?.flatMap((_p) => {
                let i = _p?.form1?.services?.map((ser) => ({
                  label: ser?.servicetitle,
                  value: ser?.servicenumber,
                }));
                return i || [];
              })}
            value={selectedService}
            placeholder="Select Service"
            onChange={onChangeService}
            rightSectionPointerEvents="none"
            rightSection={icon}
            mt={10}
            disabled={!selectedClient}
          />
        )}

        {selectedType === "client" && selectedClient && !isLocationLoading && userLocation && (
          getClientAddressOptions(allPatients?.find((_p) => _p?.id === selectedClient)).length > 0 ? (
            <Select
              label="Select Address"
              placeholder="Select service location..."
              size="md"
              data={getClientAddressOptions(
                allPatients?.find((_p) => _p?.id === selectedClient)
              )}
              value={selectedPickupLocation}
              onChange={handlePickupLocationChange}
              rightSectionPointerEvents="none"
              rightSection={icon}
              mt={10}
            />
          ) : (
            <Text fz={13} color="red" mt={10}>
              No address found for this client. Please update the client profile.
            </Text>
          )
        )}

        <p className={classes.warn}>{warningText}</p>

         {/* Location status banner */}
        {isLocationLoading ? (
          <Flex align="center" gap={6} mt={8}>
            <Loader size="xs" color="gray" />
            <Text fz={12} color="dimmed">Detecting your location…</Text>
          </Flex>
        ) : !userLocation ? (
          <Flex
            mt={8}
            p={10}
            style={{
              background: "#FFF3CD",
              borderRadius: 8,
              border: "1px solid #FFC107",
            }}
            align="center"
            justify="space-between"
          >
            <Text fz={13} color="#856404">
              Please enable browser location to check in.
            </Text>
            <Button
              size="xs"
              variant="outline"
              color="yellow"
              style={{ borderColor: "#856404", color: "#856404" }}
              onClick={async () => {
                setIsLocationLoading(true);
                const loc = await getDeviceLocation();
                if (loc) {
                  setUserLocation(loc);
                  setLocationPermission("granted");
                } else {
                  setLocationPermission("denied");
                }
                setIsLocationLoading(false);
              }}
            >
              Retry
            </Button>
          </Flex>
        ) : null}

        {(checkInTypes.has("facility") || checkInTypes.has("client")) && (
          <Box className={classes.distanceSection}>
            <Box
              className={classes.distanceSectionBox}
              style={{ borderBottom: "1px solid #E5E5E5" }}
            >
              <Text fw={700} ff={"Inter"} fz={14}>
                {" "}
                Address
              </Text>
              {isClockLoading ? (
                <Skeleton height={14} radius="sm" mt={4} />
              ) : userLocation && (selectedAddress || (selectedType === "client" && buildClientAddress(allPatients?.find((_p) => _p?.id === selectedClient)))) ? (
                <div style={{ fontWeight: "lighter" }}>
                  {selectedAddress || buildClientAddress(allPatients?.find((_p) => _p?.id === selectedClient))}
                </div>
              ) : null}
            </Box>
            <Box className={[classes.distanceSectionBox, classes.distance]}>
              <div
                className={classes.iconBox}
                style={{ background: milesError ? "#FEF3F2" : "#D9F2EF" }}
              >
                <img
                  src={`/assets/images/${
                    milesError ? "distance.png" : "distance_green.png"
                  }`}
                  alt="logo"
                  style={{ width: "30px", height: "30px", objectFit: "cover" }}
                />
              </div>
              {isClockLoading ? (
                <Skeleton height={14} width={100} radius="sm" />
              ) : miles ? (
                <div>{miles} Miles away</div>
              ) : null}
            </Box>
          </Box>
        )}

       

        <Flex justify="flex-end">
          <Group mt="xl">
            <Button onClick={handleClose} className={classes.cancelBtn}>
              Cancel
            </Button>
            <Button
              className={classes.action}
              onClick={clockInHandler}
              loading={isClockLoading}
              disabled={
                isLocationLoading
                  ? true
                  : !userLocation
                    ? true
                    : selectedType === "client" && !selectedClient
                      ? true
                      : selectedType === "client" && selectedClient &&
                        getClientAddressOptions(allPatients?.find((_p) => _p?.id === selectedClient)).length === 0
                        ? true
                        : selectedType !== "client" && !selectedFacility
                          ? true
                          : selectedFacility && warningText
                            ? true
                            : milesError
                              ? true
                              : false
              }
            >
              Clock-in
            </Button>
          </Group>
        </Flex>
      </Modal>
    </>
  );
}

export default SelectCheckInType;

